// YOSHI-10464: the syntax colouring of every code block of the developer portal.
//
// It is written by hand on purpose. A highlighting library would ship grammars for dozens of
// languages the portal will never render, to paint the six it does: the five the code generator
// emits (bash for cURL, JavaScript, Python, PHP and C#) plus the JSON of the response examples.
//
// It is deliberately shallow: strings, numbers, comments, reserved words and, in JSON, property
// names. That is what makes a snippet readable at a glance; anything beyond it would be a parser,
// and a parser that is wrong about a language paints lies.
//
// The hard rule is that it never alters the code. Every token carries a verbatim slice of the
// source and the slices come out in order, so joining them returns the input character for
// character. The copy button keeps copying the raw string, never the painted markup, and the
// tokens are rendered as React elements, so no snippet is ever injected as HTML.

import { DocsCodeToken, DocsSyntax, DocsTokenKind } from "../interfaces";

interface SyntaxRules {
  /** Markers that open a comment running to the end of the line. */
  lineComments: string[];
  /** Whether a slash-star block opens a comment. */
  hasBlockComments: boolean;
  /** Delimiters that open a string literal. */
  quotes: string[];
  /** Delimiters that do not honour backslash escapes, such as the single quote of a shell. */
  rawQuotes: string[];
  keywords: Set<string>;
  /** Whether a string followed by a colon is a property name, which only JSON needs. */
  detectProperties: boolean;
}

const BASH_KEYWORDS: string[] = [
  "case",
  "do",
  "done",
  "elif",
  "else",
  "esac",
  "export",
  "fi",
  "for",
  "function",
  "if",
  "in",
  "local",
  "return",
  "then",
  "while",
];

const JAVASCRIPT_KEYWORDS: string[] = [
  "async",
  "await",
  "catch",
  "class",
  "const",
  "else",
  "export",
  "false",
  "finally",
  "for",
  "from",
  "function",
  "if",
  "import",
  "let",
  "new",
  "null",
  "of",
  "return",
  "throw",
  "true",
  "try",
  "typeof",
  "undefined",
  "var",
];

const PYTHON_KEYWORDS: string[] = [
  "False",
  "None",
  "True",
  "and",
  "as",
  "class",
  "def",
  "elif",
  "else",
  "except",
  "finally",
  "for",
  "from",
  "if",
  "import",
  "in",
  "lambda",
  "not",
  "or",
  "pass",
  "raise",
  "return",
  "try",
  "while",
  "with",
];

const PHP_KEYWORDS: string[] = [
  "as",
  "catch",
  "class",
  "echo",
  "else",
  "elseif",
  "false",
  "finally",
  "foreach",
  "function",
  "if",
  "include",
  "new",
  "null",
  "print",
  "require",
  "return",
  "true",
  "try",
  "use",
  "while",
];

const CSHARP_KEYWORDS: string[] = [
  "async",
  "await",
  "bool",
  "catch",
  "class",
  "else",
  "false",
  "finally",
  "foreach",
  "if",
  "in",
  "int",
  "namespace",
  "new",
  "null",
  "private",
  "public",
  "return",
  "static",
  "string",
  "true",
  "try",
  "using",
  "var",
  "void",
];

const JSON_KEYWORDS: string[] = ["false", "null", "true"];

const SYNTAX_RULES: Record<DocsSyntax, SyntaxRules> = {
  bash: {
    lineComments: ["#"],
    hasBlockComments: false,
    quotes: ['"', "'"],
    rawQuotes: ["'"],
    keywords: new Set(BASH_KEYWORDS),
    detectProperties: false,
  },
  javascript: {
    lineComments: ["//"],
    hasBlockComments: true,
    quotes: ['"', "'", "`"],
    rawQuotes: [],
    keywords: new Set(JAVASCRIPT_KEYWORDS),
    detectProperties: false,
  },
  python: {
    lineComments: ["#"],
    hasBlockComments: false,
    quotes: ['"', "'"],
    rawQuotes: [],
    keywords: new Set(PYTHON_KEYWORDS),
    detectProperties: false,
  },
  php: {
    lineComments: ["//", "#"],
    hasBlockComments: true,
    quotes: ['"', "'"],
    rawQuotes: [],
    keywords: new Set(PHP_KEYWORDS),
    detectProperties: false,
  },
  csharp: {
    lineComments: ["//"],
    hasBlockComments: true,
    quotes: ['"', "'"],
    rawQuotes: [],
    keywords: new Set(CSHARP_KEYWORDS),
    detectProperties: false,
  },
  json: {
    lineComments: [],
    hasBlockComments: false,
    quotes: ['"'],
    rawQuotes: [],
    keywords: new Set(JSON_KEYWORDS),
    detectProperties: true,
  },
  plain: {
    lineComments: [],
    hasBlockComments: false,
    quotes: [],
    rawQuotes: [],
    keywords: new Set<string>(),
    detectProperties: false,
  },
};

const isDigit = (character: string): boolean => {
  return character >= "0" && character <= "9";
};

const isIdentifierStart = (character: string): boolean => {
  return (
    (character >= "a" && character <= "z") ||
    (character >= "A" && character <= "Z") ||
    character === "_" ||
    character === "$"
  );
};

const isIdentifierPart = (character: string): boolean => {
  return isIdentifierStart(character) || isDigit(character);
};

/**
 * A comment marker only counts at the start of the code or after whitespace. Without this, the
 * `//` of a url written outside a string would swallow the rest of the line, and so would the `#`
 * of a shell variable such as `$#`.
 */
const opensComment = (code: string, index: number): boolean => {
  if (index === 0) {
    return true;
  }

  const previous: string = code.charAt(index - 1);

  return previous === " " || previous === "\t" || previous === "\n";
};

/**
 * Returns the index just past the closing delimiter of the string that opens at `start`.
 *
 * An unterminated literal stops at the end of its line instead of swallowing the rest of the
 * snippet: a missing quote should cost one line of colour, not the whole block.
 */
const readString = (
  code: string,
  start: number,
  quote: string,
  isRaw: boolean
): number => {
  let index: number = start + 1;

  while (index < code.length) {
    const character: string = code.charAt(index);

    if (character === "\n") {
      return index;
    }

    if (!isRaw && character === "\\") {
      index += 2;
      continue;
    }

    if (character === quote) {
      return index + 1;
    }

    index += 1;
  }

  return code.length;
};

/** Whether the string that ends at `end` is followed by the colon of a JSON property. */
const isPropertyName = (code: string, end: number): boolean => {
  let index: number = end;

  while (index < code.length) {
    const character: string = code.charAt(index);

    if (character === " " || character === "\t") {
      index += 1;
      continue;
    }

    return character === ":";
  }

  return false;
};

/** The comment marker that opens at `index`, or `null` when none does. */
const findLineComment = (
  code: string,
  index: number,
  markers: string[]
): string | null => {
  for (let position = 0; position < markers.length; position += 1) {
    const marker: string = markers[position];

    if (code.slice(index, index + marker.length) === marker) {
      return marker;
    }
  }

  return null;
};

/**
 * Splits `code` into coloured tokens. Consecutive uncoloured characters come back as a single
 * `plain` token, so a snippet costs a handful of elements and not one per character.
 */
export const highlightCode = (
  code: string,
  syntax: DocsSyntax
): DocsCodeToken[] => {
  const rules: SyntaxRules = SYNTAX_RULES[syntax];
  const tokens: DocsCodeToken[] = [];
  let plainStart: number = 0;
  let index: number = 0;

  const pushToken = (kind: DocsTokenKind, start: number, end: number): void => {
    if (start > plainStart) {
      tokens.push({ kind: "plain", text: code.slice(plainStart, start) });
    }

    tokens.push({ kind, text: code.slice(start, end) });
    plainStart = end;
  };

  while (index < code.length) {
    const character: string = code.charAt(index);

    if (
      rules.hasBlockComments &&
      character === "/" &&
      code.charAt(index + 1) === "*"
    ) {
      const closing: number = code.indexOf("*/", index + 2);
      const end: number = closing === -1 ? code.length : closing + 2;

      pushToken("comment", index, end);
      index = end;
      continue;
    }

    const marker: string | null = findLineComment(
      code,
      index,
      rules.lineComments
    );

    if (marker !== null && opensComment(code, index)) {
      const newline: number = code.indexOf("\n", index);
      const end: number = newline === -1 ? code.length : newline;

      pushToken("comment", index, end);
      index = end;
      continue;
    }

    if (rules.quotes.indexOf(character) !== -1) {
      const end: number = readString(
        code,
        index,
        character,
        rules.rawQuotes.indexOf(character) !== -1
      );
      const isProperty: boolean =
        rules.detectProperties && isPropertyName(code, end);

      pushToken(isProperty ? "property" : "string", index, end);
      index = end;
      continue;
    }

    // The whole identifier is consumed even when it is not a reserved word, so the digits of a
    // name such as `utf8` are never mistaken for a number.
    if (isIdentifierStart(character)) {
      let end: number = index + 1;

      while (end < code.length && isIdentifierPart(code.charAt(end))) {
        end += 1;
      }

      if (rules.keywords.has(code.slice(index, end))) {
        pushToken("keyword", index, end);
      }

      index = end;
      continue;
    }

    if (isDigit(character)) {
      let end: number = index + 1;

      while (end < code.length && isDigit(code.charAt(end))) {
        end += 1;
      }

      if (code.charAt(end) === "." && isDigit(code.charAt(end + 1))) {
        end += 1;

        while (end < code.length && isDigit(code.charAt(end))) {
          end += 1;
        }
      }

      pushToken("number", index, end);
      index = end;
      continue;
    }

    index += 1;
  }

  if (code.length > plainStart) {
    tokens.push({ kind: "plain", text: code.slice(plainStart) });
  }

  return tokens;
};
